import {
  Drawer,
  Typography,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Button,
  RadioGroup,
  Radio,
  TextField,
  Autocomplete,
  Box,
} from '@mui/material'
import { Close, Person, Room, CalendarToday } from '@mui/icons-material'
import Select from 'components/molecules/Select/Select'
import { FilterDrawerProps } from './FilterDrawer.types'
import useFilterDrawer from './FilterDrawer.logic'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'lib/dayjs'
import './FilterDrawer.scss'

const FilterDrawer = ({
  open,
  onClose,
  filters,
  selectedFilters,
  selectedCategory,
  vendorOptions,
  companyFilterValue,
  setCompanyFilterValue,
  handleFilterClick,
  handleSubFilterClick,
  handleClearAllFilters,
  surgeonFilterValue,
  patientFilterValue,
  roomFilterValue,
  dateFilterValue,
  handleSurgeonFilterChange,
  handlePatientFilterChange,
  handleRoomFilterChange,
  handleDateFilterChange,
  surgeonOptions,
  roomOptions,
  creationDateFilterValue,
  handleCreationDateFilterChange,
}: FilterDrawerProps) => {
  const { statusFilters, selectedCategoryFilter } = useFilterDrawer(
    filters,
    selectedCategory
  )

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="filter-drawer"
    >
      <div className="filter-drawer-header">
        <Typography variant="h6">Filters</Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </div>
      <Divider />

      <div className="filter-drawer-content">
        <div className="filter-section">
          <Typography variant="subtitle2" color="textSecondary">
            Vendor
          </Typography>
          <Select
            label=""
            fullWidth
            options={vendorOptions}
            onClear={() => setCompanyFilterValue(undefined)}
            onSelect={(value) => setCompanyFilterValue(value)}
            selectedValue={companyFilterValue?.name}
            placeholder="Select vendor..."
          />
        </div>

        <Divider />

        <div className="filter-section">
          <Typography variant="subtitle2" color="textSecondary">
            Tray Category
          </Typography>
          <RadioGroup
            value={selectedCategory || ''}
            onChange={(e) => {
              const filter = filters.find(
                (f) => f.filterValue === e.target.value
              )
              if (filter) handleFilterClick(filter)
            }}
          >
            {filters.map((filter) => (
              <FormControlLabel
                key={filter.filterValue}
                value={filter.filterValue}
                control={<Radio />}
                label={filter.title}
              />
            ))}
          </RadioGroup>
        </div>

        <div className="filter-section">
          <Typography variant="subtitle2" color="textSecondary">
            Tray Status
          </Typography>
          <FormGroup>
            {statusFilters.map((subFilter) => (
              <FormControlLabel
                key={subFilter.filterValue}
                control={
                  <Checkbox
                    checked={selectedFilters.has(subFilter.filterValue)}
                    onChange={() => handleSubFilterClick(subFilter.filterValue)}
                    size="small"
                  />
                }
                label={subFilter.title}
              />
            ))}
          </FormGroup>
        </div>

        <div className="filter-section">
          <Typography variant="subtitle1" fontWeight={600}>
            Case Information
          </Typography>

          <Autocomplete
            options={surgeonOptions}
            getOptionLabel={(option) => option.name}
            value={
              surgeonFilterValue
                ? { id: surgeonFilterValue, name: surgeonFilterValue }
                : null
            }
            onChange={(_, newValue) => {
              if (newValue) {
                handleSurgeonFilterChange({
                  target: { value: newValue.name },
                } as any)
              } else {
                handleSurgeonFilterChange({ target: { value: '' } } as any)
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Surgeon"
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <Person color="action" fontSize="small" sx={{ mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            label="Patient"
            variant="outlined"
            size="small"
            fullWidth
            value={patientFilterValue}
            onChange={handlePatientFilterChange}
            InputProps={{
              startAdornment: (
                <Person color="action" fontSize="small" sx={{ mr: 1 }} />
              ),
            }}
          />

          <Autocomplete
            options={roomOptions}
            getOptionLabel={(option) => option.name}
            value={
              roomFilterValue
                ? { id: roomFilterValue, name: roomFilterValue }
                : null
            }
            onChange={(_, newValue) => {
              if (newValue) {
                handleRoomFilterChange({
                  target: { value: newValue.name },
                } as any)
              } else {
                handleRoomFilterChange({ target: { value: '' } } as any)
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Room"
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <Room color="action" fontSize="small" sx={{ mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Box>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ mb: 1 }}
            >
              Date of Service Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From"
                  value={
                    dateFilterValue.startDate
                      ? dayjs(dateFilterValue.startDate, 'MM/DD/YYYY')
                      : null
                  }
                  onChange={(newValue) => {
                    handleDateFilterChange({
                      startDate: newValue
                        ? newValue.format('MM/DD/YYYY')
                        : null,
                      endDate: dateFilterValue.endDate,
                    })
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      InputProps: {
                        startAdornment: (
                          <CalendarToday
                            color="action"
                            fontSize="small"
                            sx={{ mr: 1 }}
                          />
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To"
                  value={
                    dateFilterValue.endDate
                      ? dayjs(dateFilterValue.endDate, 'MM/DD/YYYY')
                      : null
                  }
                  onChange={(newValue) => {
                    handleDateFilterChange({
                      startDate: dateFilterValue.startDate,
                      endDate: newValue ? newValue.format('MM/DD/YYYY') : null,
                    })
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      InputProps: {
                        startAdornment: (
                          <CalendarToday
                            color="action"
                            fontSize="small"
                            sx={{ mr: 1 }}
                          />
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ mb: 1 }}
            >
              Drop-off Date Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From"
                  value={
                    creationDateFilterValue.startDate
                      ? dayjs(creationDateFilterValue.startDate, 'MM/DD/YYYY')
                      : null
                  }
                  onChange={(newValue) => {
                    handleCreationDateFilterChange({
                      startDate: newValue
                        ? newValue.format('MM/DD/YYYY')
                        : null,
                      endDate: creationDateFilterValue.endDate,
                    })
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      InputProps: {
                        startAdornment: (
                          <CalendarToday
                            color="action"
                            fontSize="small"
                            sx={{ mr: 1 }}
                          />
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To"
                  value={
                    creationDateFilterValue.endDate
                      ? dayjs(creationDateFilterValue.endDate, 'MM/DD/YYYY')
                      : null
                  }
                  onChange={(newValue) => {
                    handleCreationDateFilterChange({
                      startDate: creationDateFilterValue.startDate,
                      endDate: newValue ? newValue.format('MM/DD/YYYY') : null,
                    })
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      InputProps: {
                        startAdornment: (
                          <CalendarToday
                            color="action"
                            fontSize="small"
                            sx={{ mr: 1 }}
                          />
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </div>

        <div className="filter-actions">
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={handleClearAllFilters}
          >
            Reset Filters
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

export default FilterDrawer
